import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface CustomQuestion {
  questionNumber: number;
  numberOfAnswers: number;
}

interface Question {
  questionNumber: number;
  numberOfAnswers: number;
  partIndex: number;
}

interface part {
  titre: string;
  numberOfQuestions: number;
  numberOfAnswers: number;
  customQuestions?: CustomQuestion[];
}

interface Template {
  id: number;
  title: string;
  total_questions: number;
  default_answers: number;
  user_id: number | null;  // Ajouté pour différencier public/privé
  type_id: number;  // 1 = simple, 2 = detailed
  created_at: string;
}

interface TemplatePart {
  id: number;
  template_id: number;
  titre: string;
  ordre: number;
}

interface TemplateQuestion {
  id: number;
  template_id: number;
  question_number: number;
  number_of_answers: number;
  part_id: number | null;
}

const CreateTest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [testTitle, setTestTitle] = useState<string>('');
  const [testType, setTestType] = useState<'simple' | 'detailed'>('simple');
  const [parts, setparts] = useState<part[]>([]);
  const [newpartTitre, setNewpartTitre] = useState<string>('');
  const [newpartQuestions, setNewpartQuestions] = useState<string>('');
  const [newpartAnswers, setNewpartAnswers] = useState<string>('4');

  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [userDefaultTemplateId, setUserDefaultTemplateId] = useState<number | null>(null);

  // Filtrer les templates selon le type de test sélectionné
  const filteredTemplates = useMemo(() => {
    const typeId = testType === 'simple' ? 1 : 2;
    return templates.filter(t => t.type_id === typeId);
  }, [templates, testType]);

  // Réinitialiser la sélection de template quand le type de test change
  useEffect(() => {
    if (selectedTemplateId && selectedTemplateId !== 'none') {
      const selectedTemplate = templates.find(t => t.id === parseInt(selectedTemplateId));
      const expectedTypeId = testType === 'simple' ? 1 : 2;

      // Si le template sélectionné ne correspond pas au type actuel, réinitialiser
      if (selectedTemplate && selectedTemplate.type_id !== expectedTypeId) {
        setSelectedTemplateId('none');
        setparts([]);
      }
    }
  }, [testType]);

  // Charger les templates et les infos utilisateur au montage du composant
  useEffect(() => {
    fetchUserInfo();
    fetchTemplates();
  }, []);

  // Charger le template par défaut de l'utilisateur ou aucun template
  useEffect(() => {
    if (templates.length > 0 && selectedTemplateId === '') {
      const expectedTypeId = testType === 'simple' ? 1 : 2;

      // Si l'utilisateur a un template par défaut et qu'il existe dans la liste avec le bon type
      if (userDefaultTemplateId) {
        const defaultTemplate = templates.find(t => t.id === userDefaultTemplateId);
        if (defaultTemplate && defaultTemplate.type_id === expectedTypeId) {
          setSelectedTemplateId(userDefaultTemplateId.toString());
        } else {
          setSelectedTemplateId('none');
        }
      } else {
        // Sinon, ne sélectionner aucun template
        setSelectedTemplateId('none');
      }
    }
  }, [templates, userDefaultTemplateId, testType]);

  // Charger le template sélectionné
  useEffect(() => {
    if (selectedTemplateId && selectedTemplateId !== '' && selectedTemplateId !== 'none') {
      loadTemplate(parseInt(selectedTemplateId));
    } else if (selectedTemplateId === 'none') {
      // Réinitialiser les parts si "Aucun template" est sélectionné
      setparts([]);
    }
  }, [selectedTemplateId]);

  const fetchUserInfo = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('common.error'));
      }

      const data = await response.json();
      if (data.template_id) {
        setUserDefaultTemplateId(data.template_id);
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const fetchTemplates = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/my-templates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('common.error'));
      }

      const data = await response.json();
      setTemplates(data.templates);
      setLoadingTemplates(false);
    } catch (err) {
      console.error('Erreur:', err);
      setLoadingTemplates(false);
    }
  };

  const loadTemplate = async (templateId: number): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('common.error'));
      }

      const data = await response.json();

      // Ne pas mettre à jour le titre - laisser l'utilisateur le définir
      // setTestTitle(data.template.title);

      // Reconstruire les parts à partir des données du template
      const templateParts: TemplatePart[] = data.parts;
      const templateQuestions: TemplateQuestion[] = data.questions;

      // Grouper les questions par part
      const partsMap = new Map<number, { titre: string; questions: TemplateQuestion[] }>();

      templateParts.forEach(part => {
        partsMap.set(part.id, { titre: part.titre, questions: [] });
      });

      templateQuestions.forEach(question => {
        if (question.part_id && partsMap.has(question.part_id)) {
          partsMap.get(question.part_id)!.questions.push(question);
        }
      });

      // Convertir en format part
      const loadedparts: part[] = [];
      templateParts.forEach(part => {
        const partData = partsMap.get(part.id);
        if (partData) {
          const questions = partData.questions;
          // Calculer le nombre de réponses le plus courant pour cette part
          const answerCounts: { [key: number]: number } = {};
          questions.forEach(q => {
            answerCounts[q.number_of_answers] = (answerCounts[q.number_of_answers] || 0) + 1;
          });
          const mostCommonAnswers = questions.length > 0
            ? parseInt(Object.keys(answerCounts).reduce((a, b) =>
                answerCounts[parseInt(a)] > answerCounts[parseInt(b)] ? a : b
              ))
            : data.template.default_answers;

          // Récupérer la liste des questions avec un nombre de réponses différent
          const customQuestions: CustomQuestion[] = questions
            .filter(q => q.number_of_answers !== mostCommonAnswers)
            .map(q => ({
              questionNumber: q.question_number,
              numberOfAnswers: q.number_of_answers
            }));

          loadedparts.push({
            titre: part.titre,
            numberOfQuestions: questions.length,
            numberOfAnswers: mostCommonAnswers,
            customQuestions: customQuestions.length > 0 ? customQuestions : undefined
          });
        }
      });

      setparts(loadedparts);
    } catch (err) {
      console.error(t('common.error') + ':', err);
    }
  };

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  // Vérification en temps réel que les valeurs sont des entiers valides
  const isValidInteger = (value: string): boolean => {
    const num = parseInt(value, 10);
    return !isNaN(num) && Number.isInteger(num) && num > 0 && value === num.toString();
  };

  const isValidAnswers = (value: string): boolean => {
    if (value === '') return false;
    const num = parseInt(value, 10);
    return isValidInteger(value) && num >= 2 && num <= 10;
  };

  // Validation des champs de nouvelle part
  const isNewpartValid = useMemo(() => {
    return (
      newpartTitre.trim() !== '' &&
      newpartQuestions !== '' && isValidInteger(newpartQuestions) &&
      newpartAnswers !== '' && isValidAnswers(newpartAnswers)
    );
  }, [newpartTitre, newpartQuestions, newpartAnswers]);

  // Gestion des parts
  const handleAddpart = (): void => {
    if (isNewpartValid) {
      setparts([...parts, {
        titre: newpartTitre.trim(),
        numberOfQuestions: parseInt(newpartQuestions, 10),
        numberOfAnswers: parseInt(newpartAnswers, 10)
      }]);
      setNewpartTitre('');
      setNewpartQuestions('');
      setNewpartAnswers('4');
    }
  };

  const handleRemovepart = (index: number): void => {
    setparts(parts.filter((_, i) => i !== index));
  };

  // Calculer le nombre total de questions
  const totalQuestions = useMemo(() => {
    return parts.reduce((sum, part) => sum + part.numberOfQuestions, 0);
  }, [parts]);

  // Le formulaire est valide si le titre est rempli et qu'il y a au moins une part
  const isFormValid = useMemo(() => {
    return testTitle.trim() !== '' && parts.length > 0;
  }, [testTitle, parts]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!isFormValid) return;

    if (testType === 'detailed') {
      // Pour les tests détaillés, construire les questions et aller directement à la configuration détaillée
      const questions: Question[] = [];
      let questionNumber = 1;
      parts.forEach((part, partIndex) => {
        for (let i = 0; i < part.numberOfQuestions; i++) {
          questions.push({
            questionNumber: questionNumber++,
            numberOfAnswers: part.numberOfAnswers,
            partIndex
          });
        }
      });
      navigate('/detailed-test-configuration', {
        state: { testTitle, testType, questions, parts }
      });
    } else {
      // Pour les tests simples, passer par configure-test
      navigate('/configure-test', {
        state: {
          testTitle,
          testType,
          parts: parts,
          templateId: (selectedTemplateId && selectedTemplateId !== 'none') ? parseInt(selectedTemplateId) : null
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">{t('tests.create.title')}</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
              <FileText className="text-white w-8 h-8" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800">{t('tests.create.newTest')}</h2>
              <p className="text-gray-600">{t('tests.create.subtitle')}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Sélection du type de test */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Type de test
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setTestType('simple')}
                  className={`p-4 border-2 rounded-lg transition ${
                    testType === 'simple'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <h3 className="font-bold text-lg mb-2">Test Simple</h3>
                  <p className="text-sm text-gray-600">
                    Questions numérotées avec réponses lettres (A, B, C, D)
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setTestType('detailed')}
                  className={`p-4 border-2 rounded-lg transition ${
                    testType === 'detailed'
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <h3 className="font-bold text-lg mb-2">Test Détaillé</h3>
                  <p className="text-sm text-gray-600">
                    Questions avec titres et choix de réponses descriptifs
                  </p>
                </button>
              </div>
            </div>

            {/* Sélection du template */}
            {!loadingTemplates && filteredTemplates.length > 0 && (
              <div>
                <label htmlFor="templateSelect" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tests.create.loadTemplate')}
                </label>
                <select
                  id="templateSelect"
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="none">{t('tests.create.noTemplate')}</option>
                  {filteredTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.title} ({template.total_questions} {t('tests.create.questionsPlural')})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {loadingTemplates && (
              <div className="text-center py-4 text-gray-600">
                {t('tests.create.loadingTemplates')}
              </div>
            )}

            {/* Titre du test */}
            <div>
              <label htmlFor="testTitle" className="block text-sm font-medium text-gray-700 mb-2">
                {t('tests.create.testTitle')}
              </label>
              <input
                type="text"
                id="testTitle"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('tests.create.enterTitle')}
                required
              />
            </div>

            {/* Section des parts */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('tests.create.sections')}
              </label>
              <p className="text-sm text-gray-500 mb-4">
                {t('tests.create.sectionsDescription')}
              </p>

              {/* Liste des parts ajoutées */}
              {parts.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="bg-gray-100 px-4 py-2 rounded-lg font-semibold text-sm">
                    {t('tests.create.totalQuestions')}: {totalQuestions} {totalQuestions > 1 ? t('tests.create.questionsPlural') : t('tests.create.question')}
                  </div>
                  {parts.map((part, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-blue-50 px-4 py-3 rounded-lg"
                    >
                      <div className="flex-1">
                        <span className="text-gray-800 font-medium block">
                          {t('tests.create.section')} {index + 1}: {part.titre}
                        </span>
                        <span className="text-sm text-gray-600">
                          {part.numberOfQuestions} {part.numberOfQuestions > 1 ? t('tests.create.questionsPlural') : t('tests.create.question')} • {part.numberOfAnswers} {part.numberOfAnswers > 1 ? t('tests.create.answersPlural') : t('tests.create.answer')} {t('tests.create.defaultAnswers')}
                          {part.customQuestions && part.customQuestions.length > 0 && (() => {
                            // Grouper les questions par nombre de réponses
                            const grouped: { [key: number]: number } = {};
                            part.customQuestions.forEach(q => {
                              grouped[q.numberOfAnswers] = (grouped[q.numberOfAnswers] || 0) + 1;
                            });

                            // Créer le texte résumé
                            const summary = Object.entries(grouped)
                              .map(([answers, count]) => `${count} ${count > 1 ? t('tests.create.questionsPlural') : t('tests.create.question')} avec ${answers} ${parseInt(answers) > 1 ? t('tests.create.answersPlural') : t('tests.create.answer')}`)
                              .join(', ');

                            return <> • {summary}</>;
                          })()}
                        </span>
                      </div>
                      {selectedTemplateId === 'none' && (
                        <button
                          type="button"
                          onClick={() => handleRemovepart(index)}
                          className="text-red-600 hover:text-red-800 transition ml-4"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Formulaire d'ajout de part */}
              {selectedTemplateId === 'none' && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      {t('tests.create.sectionTitle')}
                    </label>
                    <input
                      type="text"
                      value={newpartTitre}
                      onChange={(e) => setNewpartTitre(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder={t('tests.create.sectionTitlePlaceholder')}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('tests.create.numberOfQuestions')}
                      </label>
                      <input
                        type="text"
                        value={newpartQuestions}
                        onChange={(e) => setNewpartQuestions(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                          newpartQuestions === '' || isValidInteger(newpartQuestions)
                            ? 'border-gray-300 focus:ring-blue-500'
                            : 'border-red-500 focus:ring-red-500'
                        }`}
                        placeholder={t('tests.create.numberOfQuestionsPlaceholder')}
                      />
                      {newpartQuestions !== '' && !isValidInteger(newpartQuestions) && (
                        <p className="mt-1 text-xs text-red-600">{t('tests.create.validationIntegerPositive')}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {t('tests.create.possibleAnswers')}
                      </label>
                      <input
                        type="text"
                        value={newpartAnswers}
                        onChange={(e) => setNewpartAnswers(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 text-sm ${
                          newpartAnswers === '' || isValidAnswers(newpartAnswers)
                            ? 'border-gray-300 focus:ring-blue-500'
                            : 'border-red-500 focus:ring-red-500'
                        }`}
                        placeholder={t('tests.create.possibleAnswersPlaceholder')}
                      />
                      {newpartAnswers !== '' && !isValidAnswers(newpartAnswers) && (
                        <p className="mt-1 text-xs text-red-600">{t('tests.create.validationBetween2And10')}</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleAddpart}
                    disabled={!isNewpartValid}
                    className={`w-full py-2 rounded-lg font-semibold transition flex items-center justify-center gap-2 text-sm ${
                      isNewpartValid
                        ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                    {t('tests.create.addSection')}
                  </button>
                </div>
              )}
            </div>

            {/* Bouton d'action */}
            <div className="flex justify-end pt-6">
              <button
                type="submit"
                disabled={!isFormValid}
                className={`px-8 py-3 rounded-lg font-semibold transition ${
                  isFormValid
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {t('tests.create.configureTest')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateTest;
